import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { financeService } from '../services/api';
import MessagesSidebar from '../components/MessagesSidebar';

/* ─── küçük yardımcılar ─── */
const TextInput = ({ placeholder, value, onChange, type = 'text' }) => (
    <input
        type={type}
        value={value ?? ''}
        onChange={onChange}
        placeholder={placeholder}
        dir="rtl"
        className="w-full bg-white border border-[#D6E1F5] rounded-xl px-4 py-2.5 text-xs sm:text-sm text-[#204286] placeholder:text-gray-300 focus:outline-none focus:ring-2 focus:ring-[#292DD8]/30 transition-all"
    />
);

const FieldLabel = ({ children }) => (
    <p className="text-xs sm:text-sm font-bold text-[#204286] text-right mb-1.5">{children}</p>
);

const Toast = ({ message, type, onClose }) => (
    <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 40 }}
        className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-6 py-3 rounded-xl text-white text-sm font-bold shadow-xl ${
            type === 'success' ? 'bg-green-500' : 'bg-red-500'
        }`}
    >
        <div className="flex items-center gap-2">
            <span>{message}</span>
            <button onClick={onClose} className="opacity-70 hover:opacity-100">✕</button>
        </div>
    </motion.div>
);

/* radio custom */
const RadioOption = ({ label, value, selected, onChange }) => (
    <label className="flex items-center gap-2.5 cursor-pointer" onClick={() => onChange(value)}>
        <span className="text-sm sm:text-base text-[#204286] font-medium">{label}</span>
        <div className={`w-4 h-4 sm:w-[18px] sm:h-[18px] rounded-full border-2 flex items-center justify-center transition-all ${selected ? 'border-[#292DD8]' : 'border-gray-400'}`}>
            {selected && <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-[#292DD8]" />}
        </div>
    </label>
);

/* banka logoları — henüz API'ye bağlı değil, sadece UI */
const BANKS = [
    { id: 1, name: 'ملی' },
    { id: 2, name: 'ملت' },
    { id: 3, name: 'صادرات' },
];

const BankLogo = ({ name, selected, onSelect }) => (
    <button
        type="button"
        onClick={onSelect}
        className={`flex flex-col items-center justify-center w-24 h-16 sm:w-28 sm:h-20 rounded-xl border-2 transition-all duration-200 ${
            selected
                ? 'border-[#292DD8] bg-blue-50 shadow-md'
                : 'border-[#D6E1F5] bg-white hover:border-[#292DD8]/40'
        }`}
    >
        <div className="w-10 h-8 sm:w-12 sm:h-10 bg-gradient-to-br from-[#204286]/20 to-[#292DD8]/30 rounded-lg flex items-center justify-center mb-1">
            <svg className="w-5 h-5 text-[#204286]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
        </div>
        <span className="text-[10px] sm:text-xs font-bold text-[#204286]">بانک {name}</span>
    </button>
);

/* ─── main ─── */
const FinanceDocument = () => {
    const navigate = useNavigate();
    const { code } = useParams();
    const { language } = useLanguage();
    const isRtl = language === 'fa';
    const t = (fa, en) => (isRtl ? fa : en);

    /* پرداخت آنلاین / بارگزاری مدارک */
    const [paymentMethod, setPaymentMethod] = useState('online'); // 'online' | 'upload'
    const [selectedBank, setSelectedBank] = useState(null);

    const [form, setForm] = useState({
        code: '', bank: '', amount: '', iban: '', transfered_at: ''
    });
    const receiptFileRef = useRef(null);
    const [receiptFile, setReceiptFile] = useState(null);
    const [existingReceipt, setExistingReceipt] = useState(null);

    const [pageLoading, setPageLoading] = useState(true);
    const [pageError, setPageError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [toast, setToast] = useState(null);

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3500);
    };

    /* ── mevcut dekontu getir (varsa) ── */
    useEffect(() => {
        const init = async () => {
            setPageLoading(true);
            setPageError(null);
            try {
                const data = await financeService.getDocument(code);
                if (data) {
                    setForm({
                        code: data.code || '',
                        bank: data.bank || '',
                        amount: data.amount ?? '',
                        iban: data.iban || '',
                        transfered_at: data.transfered_at ? data.transfered_at.slice(0, 10) : ''
                    });
                    setExistingReceipt(data.receipt || null);
                }
            } catch (err) {
                // 404 = henüz sند yok, bu normal bir durum — form boş açılır
                if (err.response?.status !== 404) {
                    console.error('Document fetch error:', err);
                    setPageError(t('خطا در دریافت اطلاعات', 'Error loading document'));
                }
            } finally {
                setPageLoading(false);
            }
        };
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [code]);

    const setField = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.code.trim()) {
            showToast(t('کد پیگیری الزامی است', 'Tracking code is required'), 'error');
            return;
        }

        setSubmitting(true);
        try {
            await financeService.submitDocument(code, {
                code: form.code,
                bank: form.bank,
                amount: form.amount,
                iban: form.iban,
                transfered_at: form.transfered_at,
                receipt: receiptFile
            });
            showToast(t('سند با موفقیت ذخیره شد', 'Document saved successfully'));
        } catch (err) {
            console.error('Document submit error:', err);
            showToast(
                t('خطا در ذخیره سند. دوباره تلاش کنید.', 'Error saving document. Please try again.'),
                'error'
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="max-w-[1136px] mx-auto px-3 sm:px-4 py-4 lg:py-8 font-iransansRegular overflow-hidden"
            dir={isRtl ? 'rtl' : 'ltr'}
        >
            <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
                <MessagesSidebar
                    activeKey="finance"
                    onNavigate={(key) => {
                        if (key === 'messages') navigate('/messages');
                        if (key === 'finance') navigate('/finance');
                        if (key === 'account') navigate('/account');
                        if (key === 'support') navigate('/support');
                        if (key === 'logout') navigate('/logout');
                    }}
                />

                <div className="flex-1 w-full min-w-0">
                    {/* Title + back */}
                    <div className="flex items-center justify-between mb-5 lg:mb-7">
                        <motion.h1
                            className="text-xl sm:text-2xl lg:text-[32px] font-extrabold text-[#292DD8] text-right"
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            {t('بارگزاری مدارک پرداخت', 'Upload Payment Documents')}
                        </motion.h1>
                        <button
                            type="button"
                            onClick={() => navigate('/finance')}
                            className="text-xs sm:text-sm font-bold text-[#204286] hover:underline whitespace-nowrap"
                        >
                            {t('بازگشت به لیست', 'Back to List')}
                        </button>
                    </div>

                    {/* code chip */}
                    <p className="text-xs sm:text-sm text-gray-400 mb-5 text-right">
                        {t('کد پیگیری پرداخت', 'Payment Tracking Code')}: <span className="font-bold text-[#204286]">{code}</span>
                    </p>

                    {pageLoading && (
                        <div className="flex justify-center items-center min-h-[40vh]">
                            <div className="flex flex-col items-center gap-4">
                                <div className="w-10 h-10 border-4 border-[#292DD8] border-t-transparent rounded-full animate-spin" />
                                <p className="text-[#292DD8] font-bold text-sm">
                                    {t('در حال بارگذاری...', 'Loading...')}
                                </p>
                            </div>
                        </div>
                    )}

                    {!pageLoading && pageError && (
                        <div className="flex justify-center items-center min-h-[40vh]">
                            <div className="flex flex-col items-center gap-4 bg-red-50 rounded-xl p-8">
                                <p className="text-red-600 font-bold text-sm">{pageError}</p>
                                <button
                                    onClick={() => window.location.reload()}
                                    className="px-5 py-2 bg-red-500 text-white rounded-lg text-sm font-bold hover:bg-red-600 transition-colors"
                                >
                                    {t('تلاش مجدد', 'Try Again')}
                                </button>
                            </div>
                        </div>
                    )}

                    {!pageLoading && !pageError && (
                        <motion.form
                            onSubmit={handleSubmit}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                        >
                            {/* radio: پرداخت آنلاین / بارگزاری مدارک */}
                            <div className="flex flex-col items-end gap-2.5 mb-5">
                                <RadioOption
                                    label={t('پرداخت آنلاین', 'Online Payment')}
                                    value="online"
                                    selected={paymentMethod === 'online'}
                                    onChange={setPaymentMethod}
                                />
                                <RadioOption
                                    label={t('بارگزاری مدارک پرداخت', 'Upload Payment Documents')}
                                    value="upload"
                                    selected={paymentMethod === 'upload'}
                                    onChange={setPaymentMethod}
                                />
                            </div>

                            <AnimatePresence mode="wait">
                                {/* ── آنلاین: بانک‌ها (UI فقط - هنوز به API وصل نیست) ── */}
                                {paymentMethod === 'online' && (
                                    <motion.div
                                        key="online"
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -8 }}
                                        transition={{ duration: 0.25 }}
                                        className="bg-[#F5F7FB] rounded-2xl overflow-hidden mb-5 px-4 sm:px-5 py-5 sm:py-6"
                                    >
                                        <p className="text-xs sm:text-sm text-[#204286] text-right mb-3">
                                            {t('درگاه پرداخت را انتخاب نمایید', 'Please select a payment gateway')}
                                        </p>
                                        <div className="flex justify-end gap-3 mb-5 flex-wrap">
                                            {BANKS.map((bank) => (
                                                <BankLogo
                                                    key={bank.id}
                                                    name={bank.name}
                                                    selected={selectedBank === bank.id}
                                                    onSelect={() => setSelectedBank(bank.id)}
                                                />
                                            ))}
                                        </div>
                                        <div className="flex justify-end">
                                            <motion.button
                                                type="button"
                                                whileHover={{ scale: 1.02 }}
                                                whileTap={{ scale: 0.97 }}
                                                disabled={!selectedBank}
                                                className="px-6 py-2.5 rounded-xl text-white font-bold text-xs sm:text-sm bg-green-500 hover:bg-green-600 shadow-md transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
                                            >
                                                {t('ورود به درگاه پرداخت', 'Go to Payment Gateway')}
                                            </motion.button>
                                        </div>
                                    </motion.div>
                                )}

                                {/* ── بارگزاری مدارک: فرم سند پرداخت ── */}
                                {paymentMethod === 'upload' && (
                                    <motion.div
                                        key="upload"
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -8 }}
                                        transition={{ duration: 0.25 }}
                                    >
                                        <div className="bg-[#F5F7FB] rounded-2xl overflow-hidden mb-5 px-4 sm:px-5 py-5 sm:py-6">
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
                                                <div>
                                                    <FieldLabel>{t('کد پیگیری', 'Tracking Code')}</FieldLabel>
                                                    <TextInput
                                                        placeholder="مثال: ۱۲۳۴۵۶۷۸۹"
                                                        value={form.code}
                                                        onChange={setField('code')}
                                                    />
                                                    <p className="text-[#D0021B] text-[11px] text-right mt-1">
                                                        {t('این فیلد الزامی است', 'This field is required')}
                                                    </p>
                                                </div>
                                                <div>
                                                    <FieldLabel>{t('نام بانک', 'Bank Name')}</FieldLabel>
                                                    <TextInput
                                                        placeholder={t('مثال: بانک ملی', 'Example: Bank name')}
                                                        value={form.bank}
                                                        onChange={setField('bank')}
                                                    />
                                                    <p className="text-[#D0021B] text-[11px] text-right mt-1">
                                                        {t('«از حروف فارسی استفاده کنید»', '«Use Persian characters»')}
                                                    </p>
                                                </div>
                                                <div>
                                                    <FieldLabel>{t('مبلغ', 'Amount')}</FieldLabel>
                                                    <TextInput
                                                        placeholder="مثال: ۱۲۳۴۵۶۷۸۹"
                                                        value={form.amount}
                                                        onChange={setField('amount')}
                                                    />
                                                </div>
                                                <div>
                                                    <FieldLabel>{t('شماره شبا مبدا', 'Source IBAN')}</FieldLabel>
                                                    <TextInput
                                                        placeholder="مثال: IR123456789012345678901234"
                                                        value={form.iban}
                                                        onChange={setField('iban')}
                                                    />
                                                </div>
                                                <div>
                                                    <FieldLabel>{t('تاریخ واریز', 'Transfer Date')}</FieldLabel>
                                                    <TextInput
                                                        type="date"
                                                        value={form.transfered_at}
                                                        onChange={setField('transfered_at')}
                                                    />
                                                </div>
                                            </div>

                                            {/* بارگزاری رسید واریز */}
                                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 pt-4">
                                                <label htmlFor="receipt-file" className="cursor-pointer">
                                                    <motion.div
                                                        whileHover={{ scale: 1.02 }}
                                                        whileTap={{ scale: 0.97 }}
                                                        className="px-5 py-2 rounded-xl text-white font-bold text-xs sm:text-sm bg-green-500 hover:bg-green-600 shadow-md transition-all duration-300 whitespace-nowrap"
                                                    >
                                                        {t('بارگزاری رسید واریز', 'Upload Transfer Receipt')}
                                                    </motion.div>
                                                </label>
                                                <input
                                                    id="receipt-file"
                                                    ref={receiptFileRef}
                                                    type="file"
                                                    accept="image/*,application/pdf"
                                                    onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
                                                    className="hidden"
                                                />
                                                {receiptFile && (
                                                    <span className="text-xs text-[#204286] font-medium truncate max-w-[200px]">
                                                        {receiptFile.name}
                                                    </span>
                                                )}
                                                {!receiptFile && existingReceipt && (
                                                    <a
                                                        href={existingReceipt}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-xs text-[#292DD8] font-medium hover:underline"
                                                    >
                                                        {t('مشاهده رسید فعلی', 'View current receipt')}
                                                    </a>
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex justify-center">
                                            <motion.button
                                                type="submit"
                                                disabled={submitting}
                                                whileHover={{ scale: submitting ? 1 : 1.02 }}
                                                whileTap={{ scale: submitting ? 1 : 0.98 }}
                                                className="px-10 py-2.5 rounded-lg text-white font-bold text-sm sm:text-base shadow-lg bg-gradient-to-r from-[#292DD8] to-[#4B4FEE] hover:from-[#1f23a8] hover:to-[#3a3ecc] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                                            >
                                                {submitting
                                                    ? t('در حال ذخیره...', 'Saving...')
                                                    : t('ذخیره اطلاعات', 'Save Information')}
                                            </motion.button>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </motion.form>
                    )}
                </div>
            </div>

            <AnimatePresence>
                {toast && (
                    <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
                )}
            </AnimatePresence>
        </div>
    );
};

export default FinanceDocument;